import { useNavigate, useParams } from 'react-router';
import { JSX, useEffect, useState } from 'react';
import * as React from 'react';
import { toast } from 'react-toastify';
import { useTranslation } from 'react-i18next';
import { useSelector } from 'react-redux';

import styles from './ModuleRenderer.module.scss';
import {
  DynamicComponentProps,
  InspectionDefectChange,
  CartonDataChange,
} from '@/types/ModuleComponentProp.type';
import { useOpenInspection } from '@/hooks/useOpenInspection';
import { updateOpenedInspection } from '@/store/slices/openInspectionSlice';
import { setPageHeader } from '@/store/slices/appDataSlice';
import { store } from '@/store/store';
import { useTemplate } from '@/hooks/useTemplate';
import { ComponentStructure } from '@/types/Template.type';
import { InspectionDefect } from '@/types/InspectionDefect';
import { Image } from '@/types/Image.type';
import { generateUniqueId } from '@/utils/string.util';
import { InspectionImageType } from '@/enums/InspectionImage.enum';
import { ROUTES } from '@/route/routes.constants';
import { imageStorage } from '@/store/idbStorage/imageStorage';
import { Inspection } from '@/types/Inspection.type';
import { RootState } from '@/store/rootReducer.ts';
import { Measurement } from '@/types/Measurement.ts';
import { SaveChangesModal } from '@/features/inspection-details/components/saveChangeCard/SaveChangesModal.tsx';
import { useNavigationContext } from '@/hooks/useNavigationContext';
import {
  DIMENSION_CHECK_MODULE_NAME,
  getInspectionModule,
  getSizeChartVersion,
  hasInspectionSizeChart,
  InspectionModuleComponentMap,
  restructureSizeChartData,
  SizeMeasurement,
} from '@/utils/inspection.utils';
import { SizeChart } from '@/types/SizeChart.ts';
import { useNavigationBlocker } from '@/hooks/useNavigationBlocker';
import { useInspectionGpsCapture } from '@/hooks/useInspectionGpsCapture';
import { LockedForUploadMessage } from '@/features/inspection-details/components/locked-for-upload-message/LockedForUploadMessage';
import { useCanOpenInspection } from '@/hooks/useCanOpenInspection';
import { ActionButtons } from '@/features/inspection-details/ActionButtons';
import { Footer } from '../footer/footer';
import { CartonData } from '@/types/CartonData.type';
import { isCartonNumberInvalid } from '@/utils/validation/cartonValidation';
import { getNextCartonSequence, reindexCartonSequences } from '@/utils/cartonSequence.util';
import {
  addDefectAndCheckModule,
  deleteDefectAndUpdateChecklist,
  getChecklistModuleNames,
  withModuleChecked,
} from '@/utils/checked-modules.util';
import { validateModuleChecklistSave } from '@/utils/inspection-checklist-validation.util';
import { APP_CONFIG_KEY } from '@/types/AppConfig.type';
import { useCheckpointChecks } from '@/hooks/masterData';
import { withCheckpointCheckResult } from '@/utils/checkpoint-checks.util';

function imageTypeFromUrl(url: string): string | undefined {
  try {
    return new URL(url).searchParams.get('imageType') ?? undefined;
  } catch {
    const match = url.match(/[?&]imageType=([^&]+)/);
    return match ? decodeURIComponent(match[1]) : undefined;
  }
}

export const ModuleRenderer = () => {
  const { id, moduleName } = useParams();
  const { inspection: openedInspection } = useOpenInspection(id);
  const [currentInspection, setCurrentInspection] = useState(
    structuredClone(openedInspection) || []
  );
  const { t: translate } = useTranslation();
  const [openDialog, setOpenDialog] = useState<boolean>(false);
  const [isAddDefectVisible, setIsAddDefectVisible] = useState<boolean>(false);
  const [currentInspectionModified, setCurrentInspectionModified] = useState<boolean>(false);
  const [isChecklistInvalid, setIsChecklistInvalid] = useState<boolean>(false);
  const { setIsBlocked, setOnBlockedNavigation } = useNavigationContext();
  const data = useSelector((state: RootState) => state.inspectionsData);
  const sizeCharts = data?.sizeCharts;
  const inspectionMeasurements: Measurement[] | [] = currentInspection?.measurements ?? [];
  const navigate = useNavigate();
  const canOpenInspection = useCanOpenInspection(id);

  useInspectionGpsCapture(id);

  const hasSizeChart =
    openedInspection?.id === id &&
    hasInspectionSizeChart({
      measurements: openedInspection.measurements ?? [],
      sizeCharts,
      productId: openedInspection.productId,
    });
  const isDimensionCheckWithoutChart = moduleName === DIMENSION_CHECK_MODULE_NAME && !hasSizeChart;

  useEffect(() => {
    if (!id || moduleName !== DIMENSION_CHECK_MODULE_NAME) {
      return;
    }
    if (openedInspection?.id !== id) {
      return;
    }
    if (!hasSizeChart) {
      navigate(`${ROUTES.INSPECTIONS}/${id}`, {
        state: { skipStartWorkFlow: true },
        replace: true,
      });
    }
  }, [id, moduleName, openedInspection?.id, hasSizeChart, navigate]);

  let requiredSizeChart: SizeChart[] | [] = getSizeChartVersion(
    inspectionMeasurements,
    sizeCharts,
    currentInspection?.productId
  );
  const config = useSelector((state: RootState) => state.appConfig);
  const mandatoryModuleCheck = Boolean(config[APP_CONFIG_KEY.mandatoryModuleCheck]);
  const { checkpointChecks } = useCheckpointChecks();
  const [restructuredData, setRestructuredData] = useState<{ [key: string]: SizeMeasurement[] }>(
    restructureSizeChartData(
      requiredSizeChart,
      inspectionMeasurements,
      config['size-check']?.passes || 3
    )
  );

  // Update global navigation blocking state when local state changes
  useEffect(() => {
    setIsBlocked(currentInspectionModified);
    setOnBlockedNavigation(() => setOpenDialog);
  }, [currentInspectionModified, setIsBlocked, setOnBlockedNavigation]);

  // Block navigation when there are unsaved changes
  useNavigationBlocker({
    isBlocked: currentInspectionModified,
    onBlockedNavigation: () => setOpenDialog(true),
    message: 'You have unsaved changes. Are you sure you want to leave?',
  });

  function updateOrCreateMeasurement(
    sizeKey: string,
    prodScId: string,
    measurement: number | null,
    sampleNo: number,
    measurementId: string | null
  ) {
    setRestructuredData((prevData) => {
      const updatedData = { ...prevData };
      const sizeMeasurements = [...(updatedData[sizeKey] || [])];
      const recordIndex = sizeMeasurements.findIndex((record) => {
        const condition =
          record.sizeChartId === prodScId &&
          (!record.sampleNumber || record.sampleNumber === sampleNo);
        if (measurementId) {
          return condition && record.measurementId === measurementId;
        }
        return condition;
      });

      if (recordIndex !== -1) {
        sizeMeasurements[recordIndex] = {
          ...sizeMeasurements[recordIndex],
          measurement: measurement,
          sampleNumber: sampleNo,
          createdOn: new Date().toISOString(),
        };
      } else return updatedData;
      updatedData[sizeKey] = sizeMeasurements;

      return updatedData;
    });
  }

  function handleMeasurementChange(
    sizeKey: string,
    measurement: number | null,
    prodScId: string,
    sampleNo: number,
    measurementId: string | null
  ) {
    updateOrCreateMeasurement(sizeKey, prodScId, measurement, sampleNo, measurementId);
    if (!currentInspectionModified) {
      setCurrentInspectionModified(true);
    }
  }
  const handleCheckedModuleChange = (moduleName: string, isChecked: boolean) => {
    setCurrentInspection((prev) => {
      if (!prev) return prev;
      return {
        ...prev,
        checkedModules: withModuleChecked(prev.checkedModules, moduleName, isChecked),
      };
    });
    if (isChecked) {
      setIsChecklistInvalid(false);
    }
    if (!currentInspectionModified) {
      setCurrentInspectionModified(true);
    }
  };

  const handleCheckpointCheckChange = (checkpointCheckId: string, isChecked: boolean) => {
    setCurrentInspection((prev) => {
      if (!prev) return prev;
      return {
        ...prev,
        checkpointCheckResults: withCheckpointCheckResult(
          prev.checkpointCheckResults,
          checkpointCheckId,
          isChecked
        ),
      };
    });
    if (isChecked) {
      setIsChecklistInvalid(false);
    }
    if (!currentInspectionModified) {
      setCurrentInspectionModified(true);
    }
  };

  function setUpdatedMeasurements(data: { [key: string]: SizeMeasurement[] }): {
    sizeChartVersion: number | null;
    measurements: Measurement[];
  } {
    let sizeChartVersion: number | null = null;
    let updatedMeasurements: Measurement[] = [];
    Object.values(data).forEach((d) => {
      d.forEach((m) => {
        if (!sizeChartVersion) {
          sizeChartVersion = m.version;
        }
        if (m.measurement && m.sampleNumber) {
          const updatedMeasurement: Measurement = {
            inspectionDataId: m.inspectionDataId ?? currentInspection?.id ?? '',
            sizeChartId: m.sizeChartId,
            measurement: m.measurement,
            sampleNumber: m.sampleNumber,
            sampleDescription: m.sampleDescription || null,
            createdOn: m.createdOn,
          };
          if (m.measurementId) {
            updatedMeasurement.id = m.measurementId;
          }
          updatedMeasurements.push(updatedMeasurement);
        }
      });
    });
    return {
      measurements: updatedMeasurements,
      sizeChartVersion: updatedMeasurements.length ? sizeChartVersion : null,
    };
  }

  const template = useTemplate(openedInspection);

  const moduleStructure = template?.modules.find((module) => module.name === moduleName);
  const [inspectionModules, setInspectionModules] = useState<InspectionModuleComponentMap>();
  const handleSaveClick = () => {
    const saveValidation = validateModuleChecklistSave({
      moduleStructure,
      moduleName,
      checkedModules: currentInspection?.checkedModules,
      isMandatoryModuleCheckEnabled: mandatoryModuleCheck && !isDimensionCheckWithoutChart,
      checkpointChecks,
      checkpointCheckResults: currentInspection?.checkpointCheckResults,
      templateId: currentInspection?.template?.id,
    });
    if (!saveValidation.isValid) {
      setIsChecklistInvalid(true);
      toast.error(translate(saveValidation.errorKey ?? 'module_checklist_required'));
      return;
    }
    setIsChecklistInvalid(false);
    if (openDialog) {
      setOpenDialog(false);
    }
    const fpImages = currentInspection?.fpImages || [];
    const deletedFpImageIds = fpImages.filter((i) => i.isDeleted)?.map((i) => i.id);
    if (deletedFpImageIds?.length) {
      imageStorage.removeMultipleItems(
        currentInspection.id,
        InspectionImageType.FULL_PRODUCT,
        deletedFpImageIds
      );
    }
    const fullProductImages: Partial<Inspection> = {
      fullProductImageUrls: fpImages
        .filter((i) => !i.isNew)
        .map((i) => i.url)
        .join(','),
      newImages: fpImages
        ?.filter((i) => i.isNew && !i.isDeleted) // remove deleted images
        .map((i) => ({ ...i, data: '' })), // do not store image data with the inspection
    };

    const { measurements, sizeChartVersion } = setUpdatedMeasurements(restructuredData);
    // Prepare updated defects immutably and cleanup deleted images
    const updatedQcDefects = (currentInspection.qcDefects || []).map((d) => {
      const deletedImageIds = d.newImages?.filter((i) => i.isDeleted)?.map((i) => i.id);
      if (deletedImageIds?.length) {
        imageStorage.removeMultipleItems(
          currentInspection.id,
          InspectionImageType.DEFECT,
          deletedImageIds
        );
      }
      const cleanedNewImages = d.newImages
        ?.filter((i) => !i.isDeleted)
        .map((i) => ({ ...i, data: '' }));
      return { ...d, newImages: cleanedNewImages } as InspectionDefect;
    });

    store.dispatch(
      updateOpenedInspection({
        ...currentInspection,
        sizeChartVersion,
        fpImages: [],
        ...fullProductImages,
        measurements,
        qcDefects: updatedQcDefects,
      })
    );
    toast.success(translate('inspection_updated'));
    setCurrentInspectionModified(false);
    goToInspectionPage(false);
    // setSaveSignal(prev => !prev);
  };
  const goToInspectionPage = (openDialog: boolean = true) => {
    if (openDialog && currentInspectionModified) {
      setOpenDialog(true);
    } else {
      // Clear the modified state before navigating
      setCurrentInspectionModified(false);
      navigate(`${ROUTES.INSPECTIONS}/${id}`, {
        state: { skipStartWorkFlow: true },
        replace: true,
      });
    }
  };

  const handleDiscardChanges = () => {
    setOpenDialog(false);
    navigate(`${ROUTES.INSPECTIONS}/${id}`, {
      state: { skipStartWorkFlow: true },
      replace: true,
    });
  };
  const hasValidationErrors = !!(currentInspection?.cartonData || []).some((c) =>
    isCartonNumberInvalid(c)
  );

  const actionButtons: ActionButtons = {
    secondaryButton: {
      label: 'Back',
      onClick: goToInspectionPage,
    },
  };
  if (canOpenInspection) {
    actionButtons.primaryButton = {
      label: 'Save',
      onClick: handleSaveClick,
      isDisabled: hasValidationErrors,
    };
  }

  useEffect(() => {
    if (moduleStructure) {
      setInspectionModules(getInspectionModule(moduleStructure.name));
    }
  }, [template]);
  useEffect(() => {
    store.dispatch(setPageHeader(moduleStructure?.header || ''));
  }, [moduleStructure?.header]);

  useEffect(() => {
    if (!openedInspection?.id || currentInspection?.id !== openedInspection.id) {
      return;
    }
    const plusMissing =
      currentInspection.quantityTolerancePlus == null &&
      openedInspection.quantityTolerancePlus != null;
    const minusMissing =
      currentInspection.quantityToleranceMinus == null &&
      openedInspection.quantityToleranceMinus != null;
    const flagMissing =
      currentInspection.actualQuantityEnabled !== true &&
      openedInspection.actualQuantityEnabled === true;
    if (!plusMissing && !minusMissing && !flagMissing) {
      return;
    }
    setCurrentInspection((prev) => {
      if (!prev || prev.id !== openedInspection.id) {
        return prev;
      }
      return {
        ...prev,
        quantityTolerancePlus: plusMissing
          ? openedInspection.quantityTolerancePlus
          : prev.quantityTolerancePlus,
        quantityToleranceMinus: minusMissing
          ? openedInspection.quantityToleranceMinus
          : prev.quantityToleranceMinus,
        actualQuantityEnabled: flagMissing ? true : prev.actualQuantityEnabled,
      };
    });
  }, [
    openedInspection?.id,
    openedInspection?.quantityTolerancePlus,
    openedInspection?.quantityToleranceMinus,
    openedInspection?.actualQuantityEnabled,
    currentInspection?.id,
    currentInspection?.quantityTolerancePlus,
    currentInspection?.quantityToleranceMinus,
    currentInspection?.actualQuantityEnabled,
  ]);

  useEffect(() => {
    const urls = openedInspection?.fullProductImageUrls
      ? openedInspection.fullProductImageUrls.split(',')
      : [];
    const images = urls.map((u) => ({
      id: generateUniqueId(),
      url: u,
      type: InspectionImageType.FULL_PRODUCT,
      imageType: imageTypeFromUrl(u),
    }));
    const pendingImages = openedInspection?.newImages || [];
    setCurrentInspection((prev) => ({ ...prev, fpImages: [...images, ...pendingImages] }));
  }, [openedInspection?.id]);

  const onValueChange = (event: React.ChangeEvent<HTMLInputElement | HTMLTextAreaElement>) => {
    let { name, value, type } = event.target;

    let castedValue: string | number | boolean;

    // Determine the value type based on the input type
    switch (type) {
      case 'number':
        castedValue = parseFloat(value);
        if (isNaN(castedValue)) castedValue = 0; // Fallback for invalid numbers
        break;
      default:
        castedValue = value; // Defaults to string
    }
    setCurrentInspection((prev) => ({ ...prev, [name]: castedValue }));
    setCurrentInspectionModified(true);
  };
  const onCustomValueChange = (name: string, value: string) => {
    setCurrentInspection((prev) => ({ ...prev, [name]: value }));
    setCurrentInspectionModified(true);
  };
  const onInspectionPatch = (patch: Partial<Inspection>) => {
    setCurrentInspection((prev) => ({ ...prev, ...patch }));
    setCurrentInspectionModified(true);
  };

  const checklistModuleNames =
    mandatoryModuleCheck && moduleStructure && moduleName && !isDimensionCheckWithoutChart
      ? getChecklistModuleNames({ modules: [moduleStructure] })
      : [];

  const defectsChangeHandlers: InspectionDefectChange = {
    onAdd: (defect: InspectionDefect) => {
      const createdOn = new Date().toISOString();
      const newDefect: InspectionDefect = {
        ...defect,
        isNew: true,
        createdOn,
        defectId: defect?.defectCode?.id || '',
        inspectionDataId: currentInspection?.id || defect.inspectionDataId,
        module: moduleName || defect.module || '',
      };
      setCurrentInspection((prev) => {
        const transition = addDefectAndCheckModule({
          qcDefects: prev.qcDefects ?? [],
          checkedModules: prev.checkedModules,
          defect: newDefect,
          checklistModuleNames,
          checkpointChecks,
          checkpointCheckResults: prev.checkpointCheckResults,
          templateId: prev.template?.id,
        });
        return {
          ...prev,
          qcDefects: transition.qcDefects,
          checkedModules: transition.checkedModules,
          checkpointCheckResults: transition.checkpointCheckResults,
        };
      });
      if (moduleName && checklistModuleNames.includes(moduleName)) {
        setIsChecklistInvalid(false);
      }
      setCurrentInspectionModified(true);
      if (isAddDefectVisible === true) {
        setIsAddDefectVisible(false);
      }
    },
    onDelete: (defect: InspectionDefect) => {
      setCurrentInspection((prev) => {
        const transition = deleteDefectAndUpdateChecklist({
          qcDefects: prev.qcDefects ?? [],
          checkedModules: prev.checkedModules,
          defectToDelete: defect,
          checklistModuleNames,
          checkpointChecks,
          checkpointCheckResults: prev.checkpointCheckResults,
          templateId: prev.template?.id,
        });
        return {
          ...prev,
          qcDefects: transition.qcDefects,
          checkedModules: transition.checkedModules,
          checkpointCheckResults: transition.checkpointCheckResults,
        };
      });
      setCurrentInspectionModified(true);
    },
    onEdit: (defect: InspectionDefect) => {
      defect.inspectionDataId = currentInspection?.id || defect.inspectionDataId;
      setCurrentInspection((prev) => ({
        ...prev,
        qcDefects: [
          ...(prev.qcDefects ?? []).map((d) => {
            const existingKey = d.id || d.newId;
            const incomingKey = defect.id || defect.newId;
            return existingKey && incomingKey && existingKey === incomingKey ? defect : d;
          }),
        ],
      }));
      setCurrentInspectionModified(true);
    },
    showAddDefect: (show: boolean) => {
      setIsAddDefectVisible(show);
    },
    isAddDefectVisible: isAddDefectVisible,
  };

  const cartonDataChangeHandlers: CartonDataChange = {
    onAdd: () => {
      setCurrentInspection((prev) => {
        const existingRows = prev.cartonData ?? [];
        const newCarton: CartonData = {
          id: generateUniqueId(),
          inspectionDataId: currentInspection.id,
          cartonNumber: '',
          productSize: '',
          quantityPackingList: 0,
          quantityCartonLabel: 0,
          quantityUnitCount: 0,
          cartonHeight: 0,
          cartonLength: 0,
          cartonWidth: 0,
          cartonWeight: 0,
          cartonLabelBarcode: '',
          cartonCheckImageUrls: '',
          isSealPaper: false,
          comment: '',
          sequence: getNextCartonSequence(existingRows),
        };
        return {
          ...prev,
          cartonData: [...existingRows, newCarton],
        };
      });
      setCurrentInspectionModified(true);
    },
    onEdit: (carton: CartonData, index: number) => {
      setCurrentInspection((prev) => ({
        ...prev,
        cartonData: (prev.cartonData ?? []).map((c, i) => (i === index ? carton : c)),
      }));
      setCurrentInspectionModified(true);
    },
    onDelete: (index: number) => {
      setCurrentInspection((prev) => {
        const remaining = (prev.cartonData ?? []).filter((_, i) => i !== index);
        return {
          ...prev,
          cartonData: reindexCartonSequences(remaining),
        };
      });
      setCurrentInspectionModified(true);
    },
  };

  const fullProductImageHandlers = {
    onAdd: async (img: Image): Promise<void> => {
      if (img?.data) {
        await imageStorage.setImage(
          currentInspection.id,
          InspectionImageType.FULL_PRODUCT,
          img.id,
          img.data || ''
        );
      }
      setCurrentInspection((prev) => ({ ...prev, fpImages: [...(prev.fpImages || []), img] }));
      setCurrentInspectionModified(true);
    },
    onDelete: async (img: Image) => {
      if (img.isNew) {
        await imageStorage.removeImage(
          currentInspection.id,
          InspectionImageType.FULL_PRODUCT,
          img.id
        );
      }
      setCurrentInspection((prev) => ({
        ...prev,
        fpImages: [...(prev.fpImages || []).filter((d) => d.id !== img.id)],
      }));
      setCurrentInspectionModified(true);
    },
    loadImages: async () => {
      const images: Array<Image> = [];
      if (currentInspection?.fpImages?.length) {
        for (const imgInfo of currentInspection.fpImages) {
          if (imgInfo.isNew && !imgInfo.data) {
            // load only if its not loaded yet
            const img = await imageStorage.getImage(
              currentInspection.id,
              InspectionImageType.FULL_PRODUCT,
              imgInfo.id
            );
            images.push({ ...imgInfo, data: img, type: InspectionImageType.FULL_PRODUCT });
          } else {
            images.push(imgInfo);
          }
        }
        setCurrentInspection((prev) => ({ ...prev, fpImages: images }));
        setCurrentInspectionModified(false);
      }
    },
  };

  const renderModuleComponents = (): JSX.Element[] => {
    return (
      (inspectionModules &&
        moduleStructure?.components.map((component) => {
          const Component = inspectionModules[component.name];
          if (!Component) {
            return <div key={component.name}>Component `{component.name}` not found</div>;
          }
          const compStructure =
            moduleStructure.components.find((c) => c.name === component.name) ||
            ({} as ComponentStructure);
          const props: DynamicComponentProps = {
            inspection: currentInspection,
            structure: compStructure,
            onValueChange: onValueChange,
            onCustomValueChange: onCustomValueChange,
            onInspectionPatch: onInspectionPatch,
            // saveSignal: sendSaveSignal,
            defectsChangeHandlers: defectsChangeHandlers,
            fullProductImageHandlers: fullProductImageHandlers,
            inspectionImageHandlers: fullProductImageHandlers,
            onCheckedModuleChange: handleCheckedModuleChange,
            onCheckpointCheckChange: handleCheckpointCheckChange,
            moduleName: moduleStructure?.name,
            cartonDataChangeHandlers: cartonDataChangeHandlers,
            isChecklistInvalid,
          };
          if (component.name === 'ProductDimensionCheck') {
            props.sizeMeasurements = restructuredData;
            props.measurementChangeHandler = handleMeasurementChange;
          }
          return compStructure ? (
            <div className={styles.component} key={component.name}>
              <Component {...props} />
            </div>
          ) : (
            <div key={component.name}>Component `{component.name}` not found</div>
          );
        })) ||
      []
    );
  };

  return (
    <section className={styles.scrollableContainer}>
      {!canOpenInspection && <LockedForUploadMessage />}
      {canOpenInspection && (
        <div className={styles.scrollableItem}>{inspectionModules && renderModuleComponents()}</div>
      )}
      {openDialog && (
        <SaveChangesModal
          onClose={() => setOpenDialog(false)}
          onCancel={() => setOpenDialog(false)}
          onDiscard={handleDiscardChanges}
        />
      )}
      <Footer {...actionButtons} />
    </section>
  );
};
